import React, { useEffect, useState } from "react";
import { PieChart, BarChart } from "@mantine/charts";
import { Card, Group, Button, Grid } from "@mantine/core";
import { LastFiveSoldIssues, MostSoldIssues } from "@/api/types";
import { IssueApi } from "@/api/issue.api";
import { UserApi } from "@/api/user.api";
import "./Analytics.css";

interface PieChartData {
  name: string;
  value: number;
  color: string;
  bg: string;
}

const Analytics: React.FC = () => {
  const [pieChartData, setPieChartData] = useState<PieChartData[]>();
  const [barChartData, setBarChartData] = useState<LastFiveSoldIssues[]>();
  const [bestSellingIssues, setBestSellingIssuesData] =
    useState<MostSoldIssues[]>();
  const [visibleCount, setVisibleCount] = useState(5);

  const getAllUsers = async () => {
    const response = await UserApi.getSubscriptionCount();
    if (response) {
      const { totalUsers, activeUsers, inactiveUsers } = response;
      const graphData: PieChartData[] = [
        {
          name: "Bez pretplate",
          value: Number(((inactiveUsers / totalUsers) * 100).toFixed(2)),
          color: "#eaeaea",
          bg: "bg-[#eaeaea]",
        },
        {
          name: "S pretplatom",
          value: Number(((activeUsers / totalUsers) * 100).toFixed(2)),
          color: "#004a99",
          bg: "bg-[#004a99]",
        },
      ];
      setPieChartData(graphData);
    }
  };

  const getLastFiveIssues = async () => {
    const response = await IssueApi.getLastFiveIssuesSales();
    if (response) setBarChartData(response);
  };

  const getMostSoldIssues = async () => {
    const response = await IssueApi.getMostSoldIssues();
    if (response) setBestSellingIssuesData(response);
  };

  useEffect(() => {
    getAllUsers();
    getLastFiveIssues();
    getMostSoldIssues();
  }, []);
  return (
    <div className="px-[5%]">
      <div className="text-[#0b1a29] text-2xl font-bold py-[1.5rem] h-[6rem] flex !items-center">
        Analitike
      </div>
      <div className="flex flex-col gap-6 pb-[1.5rem]">
        <Grid gutter="xl">
          <Grid.Col span={{ base: 12, md: 4, lg: 4 }}>
            <Card withBorder shadow="sm" radius="md" className="h-full">
              <Card.Section
                inheritPadding
                py="md"
                className="analytic-card-header"
              >
                <div className="analytic-card-title">
                  Udio korisnika s aktivnom pretplatom
                </div>
              </Card.Section>
              {pieChartData?.length ? (
                <>
                  <Group justify="center" className="py-3 !gap-6">
                    {pieChartData.map((type) => {
                      return (
                        <Group gap={10} key={type.name}>
                          <div className={`w-4 h-2 ${type.bg}`}></div>
                          <div className="text-[#515151] text-sm font-medium">
                            {type.name} ({type.value || 0}%)
                          </div>
                        </Group>
                      );
                    })}
                  </Group>
                  <div className="flex justify-center p-4">
                    <PieChart
                      data={pieChartData}
                      size={250}
                      withTooltip
                      tooltipDataSource="segment"
                    />
                  </div>
                </>
              ) : (
                <div className="flex mt-4 justify-center">Nema podataka...</div>
              )}
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8, lg: 8 }}>
            <Card withBorder shadow="sm" radius="md" className="h-full">
              <Card.Section
                inheritPadding
                py="md"
                className="analytic-card-header"
              >
                <div className="analytic-card-title">
                  Prodaja zadnjih 5 brojeva
                </div>
              </Card.Section>
              {barChartData?.length ? (
                <>
                  <Group className="py-3 !gap-6">
                    <Group gap={10}>
                      <div className="w-4 h-2 bg-[#004a99]"></div>
                      <div className="text-[#515151] text-sm font-medium">
                        Individualne kupovine
                      </div>
                    </Group>
                    <Group gap={10}>
                      <div className="w-4 h-2 bg-[#2178d5]"></div>
                      <div className="text-[#515151] text-sm font-medium">
                        Preplata
                      </div>
                    </Group>
                  </Group>
                  <div className="flex justify-center p-4">
                    <BarChart
                      h={300}
                      data={barChartData}
                      dataKey="issue"
                      type="stacked"
                      maxBarWidth={80}
                      series={[
                        {
                          name: "individualSold",
                          label: "Individualne kupovine",
                          color: "#004a99",
                        },
                        {
                          name: "subscriptionSold",
                          label: "Pretplata",
                          color: "#2178d5",
                        },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <div className="flex mt-4 justify-center">Nema podataka...</div>
              )}
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 8, lg: 8 }}>
            <Card withBorder shadow="sm" radius="md" className="h-full">
              <Card.Section
                inheritPadding
                py="md"
                className="analytic-card-header"
              >
                <div className="analytic-card-title">Naziv analitike</div>
              </Card.Section>

              <div className="flex mt-4 justify-center">Nema podataka...</div>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4, lg: 4 }}>
            <Card
              withBorder
              shadow="sm"
              radius="md"
              className="!px-0 !pb-0 h-full"
            >
              <Card.Section
                inheritPadding
                py="md"
                className="analytic-card-header"
              >
                <Group justify="space-between">
                  <div className="!px-4 analytic-card-title">
                    Najprodavaniji brojevi
                  </div>
                </Group>
              </Card.Section>
              {bestSellingIssues ? (
                <div className="flex flex-col justify-center h-full">
                  {bestSellingIssues?.slice(0, visibleCount).map((item) => (
                    <div
                      key={item.issue}
                      className="p-4 border-b flex justify-between"
                    >
                      <div className="text-[#515151] text-sm font-medium">
                        {item.issue}
                      </div>
                      <div className="text-right text-[#0b1a29] text-sm font-bold">
                        {item.sold}
                      </div>
                    </div>
                  ))}
                  <div className="p-3 border-b flex justify-center">
                    {visibleCount < bestSellingIssues?.length && (
                      <Button
                        variant="transparent"
                        color="#004a99"
                        className="!underline !text-sm !font-medium hover:opacity-80"
                        onClick={() => setVisibleCount(visibleCount + 5)}
                      >
                        Vidi više
                      </Button>
                    )}
                    {visibleCount > 5 && (
                      <Button
                        variant="transparent"
                        color="#004a99"
                        className="!underline !text-sm !font-medium hover:opacity-80"
                        onClick={() => setVisibleCount(5)}
                      >
                        Vidi manje
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex mt-4 justify-center">Nema podataka...</div>
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  );
};

export default Analytics;
